import { HttpResponse } from "@/shared/config/globals";
import { ClientSystemError, MoneyLinkException } from "@/shared/utils/custom-exception";
import { utilities } from "@/shared/utils/utility";
import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosRequestHeaders,
  AxiosResponse,
  ResponseType,
} from "axios";

/* タイムアウト値（ミリ秒）*/
export const Timeout = {
  Short: 10000,
  Middle: 30000,
  Long: 60000,
  VeryLong: 600000,
} as const;
export type TimeoutType = (typeof Timeout)[keyof typeof Timeout];

/**
 * HttpClientクラス
 */
export class HttpClient {
  constructor() { /**intentionally empty */ }

  /**
   * string型として型チェックしてパラメータ返却
   */
  private safeGetAsString(obj: Record<string, unknown>, key: string): string {
    const value = obj[key];

    if (value !== null && value !== undefined) {
      return String(value);
    }

    throw new MoneyLinkException({ error: ClientSystemError.InvalidResponse });
  }

  /**
   * number型として型チェックしてパラメータ返却
   * isNaNがtrueになる場合はエラー
   */
  private safeGetAsNumber(obj: Record<string, unknown>, key: string): number {
    const value = obj[key];

    if (typeof value === "number") {
      return value;
    }

    if (typeof value === "string" && utilities().isNumber(value)) {
      return Number(value);
    }

    throw new MoneyLinkException({ error: ClientSystemError.InvalidResponse });
  }

  /**
   * AxiosResponse→HttpResponseへコンバート
   */
  private async createResponse<T>(
    response: AxiosResponse
  ): Promise<HttpResponse<T>> {
    const result: HttpResponse<T> = new HttpResponse<T>();
    result.status = response.status;
    result.ok = result.status < 400

    // headerの変換処理
    for (const key in response.headers) {
      result.headers.set(key, response.headers[key]);
    }

    // 204の場合はbodyが存在しないため、処理を終了する
    if (result.status === 204) {
      return result;
    }

    result.contentType = result.headers.get("content-type") ?? "";
    result.location = result.headers.get("location") ?? "";
    if (result.headers.has("content-length")) {
      result.contentLength = Number(result.headers.get("content-length") as string);
    }

    // Blob 判定
    const isBlob =
      response.data instanceof Blob ||
      response.request?.responseType === "blob";

    // rawBody 設定（Blobは除外）
    if (!isBlob) {
      try {
        result.rawBody = JSON.stringify(response.data);
      } catch {
        result.rawBody = "";
      }
    }

    // Content-Typeによってレスポンスにbodyを含めて返却
    const contentTypeWithBody = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/json;charset=utf-8",
      "application/problem+json; charset=utf-8",
      "application/json",
      "application/octet-stream",
      "application/pdf",
      "image/png",
      "image/jpeg",
    ];
    const contentTypeWithoutBody = [
      "text/html; charset=utf-8",
      "text/plain; charset=utf-8",
      "text/plain",
    ];

    // bodyを返すケース
    if (contentTypeWithBody.some(ct => result.contentType.startsWith(ct))) {
      if (result.ok) {
        result.body = response.data as T;
      } else {
        let json;

        if (isBlob && typeof response.data.text === "function") {
          const text = await response.data.text();
          json = JSON.parse(text);
        } else {
          json = result.bodyAsObject();
        }

        result.errorType = this.safeGetAsString(json, "type");
        result.errorCode = this.safeGetAsNumber(json, "error_code");
        if ("message" in json) {
          result.errorMessage = this.safeGetAsString(json, "message");
        }
      }
      return result;
    }

    if (contentTypeWithoutBody.some(ct => result.contentType.startsWith(ct))) {
      return result;
    }

    if (result.contentType === "") {
      // オフライン時エラー
      throw new MoneyLinkException({ error: ClientSystemError.ConnectionRequired });
    }
    // TODO: 下記はWAFエラーやタイムアウトエラーを考慮しているが、それ以外については別途検討する
    throw new MoneyLinkException({ error: ClientSystemError.InvalidResponse });
  }


  /**
   * Axiosを使用してHttpRequestを行うWrapper
   */
  private async axiosRequest<T>(config: AxiosRequestConfig): Promise<HttpResponse<T>> {
    try {
      return axios(config)
        .then(async (res: AxiosResponse) => {
          return await this.createResponse<T>(res);
        })
        .catch(async (error: AxiosError) => {
          if (error.response) {
            return await this.createResponse<T>(error.response);
          } else {
            throw new MoneyLinkException({
              error: ClientSystemError.ConnectionRequired,
            });
          }
        });

      // // TODO: ログ検討
      // Log.info('[END] HttpClient get: status=${_res.status}');
    } catch (error: unknown) {
      // ToDo: エラーハンドリング
      if (axios.isAxiosError(error)) {
      //  // Axiosのエラー
      //  const axiosError = error as AxiosError;
      }
      throw new MoneyLinkException({ error: ClientSystemError.ConnectionRequired });
    }
  }


  /**
   * GETリクエスト
   */
  async get<T>(
    path: string,
    headers: AxiosRequestHeaders,
    queryParams: { [key: string]: string | number | boolean },
    timeout: TimeoutType,
    responseType: ResponseType,
  ): Promise<HttpResponse<T>> {
    // API呼び出しパラメータの組み立て
    const options: AxiosRequestConfig = {
      url: path,
      method: "GET",
      headers: headers,
      params: queryParams,
      timeout: timeout,
      responseType: responseType,
      withCredentials: true, // ソース下※1参照
    };

    // 共通の呼び出し処理
    return this.axiosRequest<T>(options);
  }

  /**
   * POSTリクエスト
   */
  async post<T>(
    path: string,
    headers: AxiosRequestHeaders,
    requestBody: { [key: string]: string | number | boolean },
    timeout: number,
    responseType: ResponseType,
  ): Promise<HttpResponse<T>> {
    // API呼び出しパラメータの組み立て
    const options: AxiosRequestConfig = {
      url: path,
      method: "POST",
      headers: headers,
      data: requestBody,
      timeout: timeout,
      responseType: responseType,
      withCredentials: true, // ソース下※1参照
    };

    // 共通の呼び出し処理
    return this.axiosRequest<T>(options);
  }

  /**
   * PUTリクエスト
   */
  async put<T>(
    path: string,
    headers: AxiosRequestHeaders,
    requestBody: { [key: string]: string | number | boolean },
    timeout: number,
    responseType: ResponseType,
  ): Promise<HttpResponse<T>> {
    // API呼び出しパラメータの組み立て
    const options: AxiosRequestConfig = {
      url: path,
      method: "PUT",
      headers: headers,
      data: requestBody,
      timeout: timeout,
      responseType: responseType,
      withCredentials: true, // ソース下※1参照
    };

    // 共通の呼び出し処理
    return this.axiosRequest<T>(options);
  }

  /**
   * DELETEリクエスト
   */
  async delete<T>(
    path: string,
    headers: AxiosRequestHeaders,
    queryParams: { [key: string]: string | number | boolean },
    timeout: number,
    responseType: ResponseType,
  ): Promise<HttpResponse<T>> {
    // API呼び出しパラメータの組み立て
    const options: AxiosRequestConfig = {
      url: path,
      method: "DELETE",
      headers: headers,
      params: queryParams,
      timeout: timeout,
      responseType: responseType,
      withCredentials: true, // ソース下※1参照
    };

    // 共通の呼び出し処理
    return this.axiosRequest<T>(options);
  }

  /**
   * GETリクエスト（Blob）
   */
  async getBlob<T>(
    path: string,
    headers: AxiosRequestHeaders,
    queryParams: { [key: string]: string | number | boolean },
    timeout: number,
    responseType: ResponseType,
  ): Promise<HttpResponse<T>> {
    // API呼び出しパラメータの組み立て
    const options: AxiosRequestConfig = {
      url: path,
      method: "GET",
      headers: headers,
      params: queryParams,
      timeout: timeout,
      responseType: responseType,
      withCredentials: true, // ソース下※1参照
    };

    // 共通の呼び出し処理
    return this.axiosRequest<T>(options);
  }

  /**
   * POSTリクエスト（Blob）
   */
  async postBlob<T>(
    path: string,
    headers: AxiosRequestHeaders,
    requestBody: NonNullable<unknown>,
    timeout: number,
    responseType: ResponseType,
  ): Promise<HttpResponse<T>> {
    // API呼び出しパラメータの組み立て
    const options: AxiosRequestConfig = {
      url: path,
      method: "POST",
      headers: headers,
      data: requestBody,
      timeout: timeout,
      responseType: responseType,
      withCredentials: true, // ソース下※1参照
    };

    // 共通の呼び出し処理
    return this.axiosRequest<T>(options);
  }

  /**
   * POSTリクエスト（画像データ）
   */
  async postBytes<T>(
    path: string,
    headers: AxiosRequestHeaders,
    requestBody: NonNullable<unknown>,
    timeout: number,
    responseType: ResponseType,
  ): Promise<HttpResponse<T>> {
    // API呼び出しパラメータの組み立て
    const options: AxiosRequestConfig = {
      url: path,
      method: "POST",
      headers: headers,
      data: requestBody,
      timeout: timeout,
      responseType: responseType,
      withCredentials: true, // ソース下※1参照
    };

    // 共通の呼び出し処理
    return this.axiosRequest<T>(options);
  }
}

/**
 * ※1 withCredentialsについて
 * クッキーを含むリクエストを送信するための設定。
 * MoneyLinkはフロントエンドとバックエンドを同一コンテナ、同一ポートで実装するため本来はこの値を指定しなくてもクッキーが送れる。
 * ただ、開発時は利便性のためフロントエンドとバックエンドをそれぞれ別に立上げているため、これを指定しないとセッション管理ができない。
 */